import DefaultLayout from "../../layout/DefaultLayout";
import BreadcrumbTwo from "../../components/Breadcrumbs/BreadcrumbTwo";
import SvgIcon from "../../components/SvgIcon/SvgIcon";

/**
 * ❓ Página “Preguntas Frecuentes”
 */
const AboutFaq = () => {
  const faqSections = [
    {
      category: "Uso General",
      items: [
        {
          title: "¿Cómo puedo registrar una nueva venta?",
          details:
            "Dirígete al módulo de Ventas, selecciona 'Registrar Venta' y completa los campos requeridos. Puedes buscar los productos desde el catálogo, escanear su código o agregarlos manualmente. Al finalizar, guarda y el sistema generará automáticamente el Documento Tributario Electrónico (DTE).",
        },
        {
          title: "¿Dónde se configura la información de mi empresa?",
          details:
            "En el módulo Configuración → Empresa, podrás actualizar los datos fiscales, logotipo, NIT, dirección y demás información general que aparecerá en tus documentos DTE y reportes internos.",
        },
        {
          title: "¿El sistema funciona sin conexión a internet?",
          details:
            "Codenova POS es una plataforma 100% en la nube, por lo que requiere conexión a internet para operar. Esto garantiza la sincronización en tiempo real y la disponibilidad desde cualquier dispositivo.",
        },
      ],
    },
    {
      category: "Facturación Electrónica (DTE)",
      items: [
        {
          title: "¿Qué significa DTE?",
          details:
            "DTE significa Documento Tributario Electrónico. Es el formato oficial requerido por el Ministerio de Hacienda de El Salvador para emitir facturas, comprobantes de crédito fiscal, notas de crédito, entre otros documentos electrónicos.",
        },
        {
          title: "¿Cómo se envían los DTE a Hacienda?",
          details:
            "El sistema se encarga automáticamente de transmitir cada documento al Ministerio de Hacienda una vez emitido, validando su estructura y firma electrónica. Si ocurre un error, el sistema mostrará la causa y podrás reenviar el documento.",
        },
        {
          title: "¿Puedo anular un DTE desde el sistema?",
          details:
            "Sí. En el módulo de Ventas encontrarás la opción 'Anular Documento'. Allí podrás seleccionar el motivo de anulación y generar el archivo de invalidación, cumpliendo con los plazos y requisitos establecidos por Hacienda.",
        },
      ],
    },
    {
      category: "Reportes y Control",
      items: [
        {
          title: "¿Cómo generar reportes de ventas y compras?",
          details:
            "En el módulo de Reportes puedes filtrar por fechas, clientes, proveedores o tipo de documento. Además, los resultados pueden exportarse en formato PDF o Excel para su análisis o respaldo contable.",
        },
        {
          title: "¿Puedo analizar el rendimiento de mi negocio?",
          details:
            "Sí. Codenova POS incluye paneles de control y estadísticas que te permiten visualizar ventas diarias, productos más vendidos, gastos y utilidades en tiempo real.",
        },
      ],
    },
    {
      category: "Seguridad y Usuarios",
      items: [
        {
          title: "¿Cómo se maneja la seguridad de la información?",
          details:
            "El sistema utiliza cifrado de datos, autenticación mediante JWT (JSON Web Tokens) y una arquitectura multi-tenant que asegura el aislamiento entre empresas. Solo los usuarios autorizados pueden acceder a la información asignada a su perfil.",
        },
        {
          title: "¿Puedo crear usuarios con distintos permisos?",
          details:
            "Sí. En el módulo de Configuración → Usuarios puedes crear y asignar roles personalizados (administrador, cajero, contador, soporte, etc.), definiendo exactamente qué módulos y funciones puede usar cada uno.",
        },
      ],
    },
    {
      category: "Soporte y Asistencia",
      items: [
        {
          title: "¿Qué hago si tengo un problema técnico?",
          details:
            "Puedes contactar a nuestro equipo de soporte directamente desde el módulo 'Soporte y Contacto' o escribiendo a nuestro correo: soporte@codenovapos.com. Nuestro equipo responderá lo antes posible.",
        },
        {
          title: "¿Recibo actualizaciones del sistema?",
          details:
            "Sí. Codenova POS se actualiza de forma automática para incorporar mejoras de rendimiento, nuevas funcionalidades y actualizaciones fiscales sin necesidad de intervención del usuario.",
        },
      ],
    },
  ];

  return (
    <DefaultLayout>
      <BreadcrumbTwo />

      <div className="flex flex-col gap-7.5">
        {/* 📘 Card principal */}
        <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          {/* 🔹 Encabezado */}
          <div className="border-b border-stroke px-4 py-6 dark:border-strokedark sm:px-6 xl:px-9">
            <h3 className="text-lg font-semibold text-black dark:text-white flex items-center gap-2">
              <SvgIcon name="info-circle" className="h-5 w-5 text-primary" />
              Preguntas Frecuentes
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-3xl leading-relaxed">
              Aquí encontrarás respuestas a las dudas más comunes sobre el uso,
              configuración y funcionamiento de <strong>Codenova POS</strong>.
            </p>
          </div>

          {/* 🔸 Contenido */}
          <div className="p-6 sm:p-8 xl:p-10">
            {faqSections.map((section, idx) => (
              <div key={idx} className="mb-10">
                {/* 🏷️ Categoría */}
                <h4 className="mb-4 text-base font-semibold text-primary">
                  {section.category}
                </h4>

                {/* Preguntas */}
                <div className="flex flex-col gap-6">
                  {section.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-4 transition-all duration-200 hover:translate-x-1 hover:scale-[1.01]"
                    >
                      {/* Ícono */}
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <SvgIcon name="info-square" className="h-6 w-6" />
                      </div>

                      {/* Contenido */}
                      <div className="flex flex-col">
                        <h5 className="mb-1.5 text-base font-semibold text-black dark:text-white">
                          {item.title}
                        </h5>
                        <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                          {item.details}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* 💬 Nota final */}
            <div className="mt-12 border-t border-stroke pt-6 text-center text-sm text-gray-500 dark:border-strokedark dark:text-gray-400">
              ¿No encontraste la respuesta que buscabas?{" "}
              <span className="text-primary font-medium">
                Escríbenos a{" "}
                <a
                  href="mailto:codenovasoportesv@gmail.com"
                  className="underline"
                >
                  codenovasoportesv@gmail.com
                </a>{" "}
                o contáctanos vía WhatsApp al{" "}
                <strong>+503 7732 0837</strong>.
              </span>
            </div>
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default AboutFaq;
